import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X, MessageSquare, Send, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const SESSION_KEY = "xion_feedback_shown";
const AUTO_DELAY_MS = 45000;

function StarRating({ stars, hover, onHover, onLeave, onSelect }) {
  return (
    <div
      className="flex gap-1 mb-5"
      onMouseLeave={onLeave}
      data-testid="feedback-stars"
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          onMouseEnter={() => onHover(n)}
          onClick={() => onSelect(n)}
          data-testid={`feedback-star-${n}`}
          aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
          className="transition-transform hover:scale-110"
        >
          <Star
            className={`w-7 h-7 transition-colors ${
              (hover || stars) >= n
                ? "fill-yellow-400 text-yellow-400"
                : "text-slate-700"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function FeedbackForm({ stars, hover, comment, submitting, userName, onHover, onLeave, onSelect, onCommentChange, onClose, onSubmit }) {
  return (
    <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00D4FF] mb-1">
        / your feedback
      </p>
      <h3 className="font-display font-bold text-lg leading-tight mb-1">
        {userName
          ? `How's your Xion experience, ${userName}?`
          : "How's your Xion experience?"}
      </h3>
      <p className="text-xs text-[#6B7280] mb-5">
        Rate the website or a service you tried.
      </p>

      <StarRating
        stars={stars}
        hover={hover}
        onHover={onHover}
        onLeave={onLeave}
        onSelect={onSelect}
      />

      <textarea
        value={comment}
        onChange={onCommentChange}
        maxLength={300}
        placeholder="Tell us more (optional)..."
        data-testid="feedback-comment"
        rows={3}
        className="w-full px-3 py-2.5 rounded-lg bg-[#F8F8F6] border border-black/[0.08] text-sm text-[#374151] placeholder:text-[#5A5A72] focus:border-[#00D4FF]/60 outline-none resize-none mb-4"
      />

      <div className="flex gap-2">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2.5 rounded-lg btn-ghost text-xs font-mono uppercase tracking-wider"
        >
          Skip
        </button>
        <button
          onClick={onSubmit}
          disabled={!stars || submitting}
          data-testid="feedback-submit-btn"
          className="flex-1 px-4 py-2.5 rounded-lg btn-primary text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40"
        >
          <Send className="w-3.5 h-3.5" />
          {submitting ? "Sending..." : "Send"}
        </button>
      </div>
    </motion.div>
  );
}

function FeedbackDone() {
  return (
    <motion.div
      key="done"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="py-4 flex flex-col items-center gap-3 text-center"
    >
      <CheckCircle2 className="w-10 h-10 text-[#00D4FF]" />
      <p className="font-display font-bold text-lg">Thank you!</p>
      <p className="text-sm text-[#5A5A72]">Your feedback helps Xion improve.</p>
    </motion.div>
  );
}

export default function FeedbackWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(0);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const t = setTimeout(() => {
      setOpen(true);
      sessionStorage.setItem(SESSION_KEY, "1");
    }, AUTO_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const handleManualOpen = () => {
    setOpen(true);
    sessionStorage.setItem(SESSION_KEY, "1");
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    setStars(0);
    setComment("");
    setDone(false);
  }, []);

  const handleSubmit = async () => {
    if (!stars) return;
    setSubmitting(true);
    try {
      await api.post("/site-feedback", {
        stars,
        comment: comment.trim() || null,
        page: window.location.pathname,
        feedback_type: "website",
      });
      setDone(true);
      setTimeout(handleClose, 2500);
    } catch (e) {
      console.error("Feedback submit error:", e);
      setDone(true);
      setTimeout(handleClose, 2500);
    } finally {
      setSubmitting(false);
    }
  };

  const userName = user && user !== false ? user.name?.split(" ")[0] : null;

  return (
    <>
      <motion.button
        onClick={handleManualOpen}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 3, duration: 0.4 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="feedback-trigger-btn"
        aria-label="Leave feedback"
        className="fixed bottom-16 right-5 z-40 w-10 h-10 rounded-full bg-white border border-[#00D4FF]/40 text-[#00D4FF] grid place-items-center shadow-lg shadow-cyan-400/10 hover:border-[#00D4FF]/80 transition-all"
      >
        <MessageSquare className="w-4 h-4" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={handleClose}
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              data-testid="feedback-panel"
              className="fixed bottom-20 right-5 z-50 w-80 glass-card rounded-2xl border border-black/[0.08] p-6 shadow-2xl shadow-black/60"
            >
              <button
                onClick={handleClose}
                data-testid="feedback-close-btn"
                className="absolute top-4 right-4 text-[#9191A8] hover:text-[#0E0E1A] transition"
              >
                <X className="w-4 h-4" />
              </button>

              <AnimatePresence mode="wait">
                {done ? (
                  <FeedbackDone />
                ) : (
                  <FeedbackForm
                    stars={stars}
                    hover={hover}
                    comment={comment}
                    submitting={submitting}
                    userName={userName}
                    onHover={setHover}
                    onLeave={() => setHover(0)}
                    onSelect={setStars}
                    onCommentChange={(e) => setComment(e.target.value)}
                    onClose={handleClose}
                    onSubmit={handleSubmit}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
